import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { RootState } from './store';
import { APIUser, PixelStreamingExperience } from '../utils/userEndpoints';

interface UserState {
  user: APIUser | null;
  activePixelStreamingExperience: PixelStreamingExperience | null;
  tempProfilePictureURL: string | null; // Will go away once integrated into APIUser
  parentUser: APIUser | null; // For sub-client mode, the parent user
}

const loadUserStateFromLocalStorage = (): UserState => {
  const stateStr = localStorage.getItem('userState');
  if (stateStr) {
    try {
      return JSON.parse(stateStr);
    } catch (e) {
      console.error('Failed to parse user state from localStorage', e);
    }
  }

  return {
    user: null,
    activePixelStreamingExperience: null,
    tempProfilePictureURL: null,
    parentUser: null,
  };
};

const initialState: UserState = loadUserStateFromLocalStorage();

export const userSlice = createSlice({
  name: 'user',
  initialState,
  reducers: {
    setUser: (state, action: PayloadAction<APIUser>) => {
      console.log("[userSlice] setUser action:", action.payload);
      state.user = action.payload;
    },
    setActivePixelStreamingExperience: (state, action: PayloadAction<PixelStreamingExperience | null>) => {
      state.activePixelStreamingExperience = action.payload;
    },
    setTempProfilePictureURL: (state, action: PayloadAction<string | null>) => {
      state.tempProfilePictureURL = action.payload;
    },
    setParentUser: (state, action: PayloadAction<APIUser>) => {
      state.parentUser = action.payload;
    },
    clearUser: (state) => {
      state.user = null;
      state.activePixelStreamingExperience = null;
      state.tempProfilePictureURL = null;
    },
    clearParentUser: (state) => {
      state.parentUser = null;
    },
    pushUser: (state, action: PayloadAction<APIUser>) => {
      // Only push if the new user is different from the current user
      if (state.user?.id !== action.payload.id) {
        state.parentUser = state.user;
        state.user = action.payload;
      }
    },
    popUser: (state) => {
      state.user = state.parentUser;
      state.parentUser = null;
    },
  }
});

export const {
  setUser,
  setActivePixelStreamingExperience,
  setTempProfilePictureURL,
  setParentUser,
  clearUser,
  clearParentUser,
  pushUser,
  popUser,
} = userSlice.actions;

export const selectUserId = (state: RootState) => state.user.user?.id;
export const selectUserEmail = (state: RootState) => state.user.user?.email;
export const selectUser = (state: RootState) => state.user.user;
export const selectActivePixelStreamingExperience = (state: RootState) => state.user.activePixelStreamingExperience;
export const selectTempProfilePictureURL = (state: RootState) => state.user.tempProfilePictureURL;
export const selectParentUser = (state: RootState) => state.user.parentUser;
export const selectIsSubClient = (state: RootState) => !!state.user.parentUser;

const persistUserState = (userState: UserState) => {
  if (userState.user) {
    localStorage.setItem('userState', JSON.stringify(userState));
  } else {
    localStorage.removeItem('userState');
  }
};

export const setupUserPersistence = (store: any) => {
  store.subscribe(() => {
    const userState = store.getState().user;
    persistUserState(userState);
  });
};

export default userSlice.reducer;
